import { useState, useMemo, useEffect } from "react";

import {
  Box,
  Typography,
  Card,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  CircularProgress,
  Collapse,
} from "@mui/material";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {
  getHistoryData,
  getHeatmapData,
} from "../../services/dashboard.api";

/* ---------------- CONSTANTS ---------------- */

const MONTHS = [
  "January", "February", "March", "April",
  "May", "June", "July", "August",
  "September", "October", "November", "December",
];

const CURRENT_YEAR = new Date().getFullYear();

const YEARS = Array.from(
  { length: 310 },
  (_, i) => CURRENT_YEAR - 25 + i
);

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/* ---------------- HELPERS ---------------- */

function pad(n) {
  return String(n).padStart(2, "0");
}

function dateKey(year, month, day) {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/* ------------------------------------------------------------------ */
/* HEATMAP COLOR — single red ramp, intensity grows with count         */
/* ------------------------------------------------------------------ */

function getHeatColor(count) {
  if (count === 0) {
    return {
      bg:        "#f9fafb",
      border:    "#e5e7eb",
      textColor: "#9ca3af",
    };
  }

  if (count <= 3) {
    return {
      bg:        "rgba(220,38,38,0.12)",
      border:    "rgba(220,38,38,0.30)",
      textColor: "#dc2626",
    };
  }

  if (count <= 6) {
    return {
      bg:        "rgba(189, 39, 39, 0.38)",
      border:    "rgba(200, 26, 26, 0.55)",
      textColor: "#7f1d1d",
    };
  }

  if (count < 10) {
    return {
      bg:        "rgba(220,38,38,0.68)",
      border:    "rgba(156, 21, 21, 0.85)",
      textColor: "#ffffff",
    };
  }

  // > 10 — deepest red
  return {
    bg:        "rgba(220,38,38,0.92)",
    border:    "rgba(220,38,38,1)",
    textColor: "#ffffff",
  };
}

/* ------------------------------------------------------------------ */
/* LEGEND — single red ramp, Less → More in one row                    */
/* ------------------------------------------------------------------ */

function HeatmapLegend() {
  const levels = [
    { bg: "rgba(220,38,38,0.12)", border: "rgba(220,38,38,0.30)"  },
    { bg: "rgba(220,38,38,0.38)", border: "rgba(220,38,38,0.55)" },
    { bg: "rgba(220,38,38,0.68)", border: "rgba(220,38,38,0.85)" },
    { bg: "rgba(220,38,38,0.92)", border: "rgba(220,38,38,1)"  },
  ];

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f3f4f6" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.8, md: 1 },
          flexWrap: "nowrap",
        }}
      >
        {/* Less */}
        <Typography
          sx={{
            fontSize: { xs: 9, md: 10 },
            fontWeight: 700,
            color: "#9ca3af",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          Less
        </Typography>

        {/* Color swatches + labels */}
        {levels.map(({ bg, border, label }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.4, md: 0.6 },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: 12, md: 14 },
                height: { xs: 12, md: 14 },
                borderRadius: "3px",
                background: bg,
                border: `1px solid ${border}`,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: 9, md: 10 },
                color: "#6b7280",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}

        {/* More */}
        <Typography
          sx={{
            fontSize: { xs: 9, md: 10 },
            fontWeight: 700,
            color: "#9ca3af",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          More
        </Typography>
      </Box>
    </Box>
  );
}

/* ---------------- MAIN COMPONENT ---------------- */

export default function AlarmHistory({ latestAlarmTime }) {
  const today = new Date();

  const [isExpanded, setIsExpanded]         = useState(false);
  const [selMonth, setSelMonth]             = useState(today.getMonth());
  const [selYear, setSelYear]               = useState(today.getFullYear());
  const [selDate, setSelDate]               = useState(
    dateKey(today.getFullYear(), today.getMonth(), today.getDate())
  );
  const [detailItem, setDetailItem]         = useState(null);
  const [historyData, setHistoryData]       = useState({});
  const [heatmapData, setHeatmapData]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  /* ---------------- FETCH HEATMAP ---------------- */

  const fetchHeatmap = async () => {
    try {
      setHeatmapLoading(true);
      const response = await getHeatmapData();
      setHeatmapData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setHeatmapLoading(false);
    }
  };

  /* ---------------- FETCH HISTORY ---------------- */

  const fetchHistoryData = async (year, month, day) => {
    try {
      setHistoryLoading(true);
      const response = await getHistoryData(
        String(year),
        String(month).padStart(2, "0"),
        String(day).padStart(2, "0")
      );
      const formattedData = {
        [dateKey(year, month - 1, day)]: response.map((item, index) => ({
          id:       index + 1,
          time:     item.time     || "NA",
          deviceId: item.deviceID || "NA",
          msgType:  item.msgType  || "NA",
          // payload:  item.data     || "NA",
        })),
      };
      setHistoryData(formattedData);
    } catch (error) {
      console.log("History Fetch Error", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  /* ---------------- EFFECTS ---------------- */

  // Always fetch heatmap on page load
  useEffect(() => {
    fetchHeatmap();
  }, []);

  // Re-fetch when new alarm arrives
  useEffect(() => {
    if (!latestAlarmTime) return;
    const [year, month, day] = selDate.split("-");
    fetchHistoryData(year, month, day);
    fetchHeatmap();
  }, [latestAlarmTime]);

  // Fetch history when selected date changes
  useEffect(() => {
    const [year, month, day] = selDate.split("-");
    fetchHistoryData(year, month, day);
  }, [selDate]);

  // Adjust date safely when month/year changes
  useEffect(() => {
    const currentDay  = new Date(selDate).getDate();
    const daysInMonth = new Date(selYear, selMonth + 1, 0).getDate();
    const safeDay     = Math.min(currentDay, daysInMonth);
    setSelDate(dateKey(selYear, selMonth, safeDay));
  }, [selMonth, selYear]);

  /* ---------------- EXPORT CSV ---------------- */

  const handleExportCSV = () => {
    if (!tableData || tableData.length === 0) {
      alert("No data present");
      return;
    }
    const headers    = ["Timestamp", "Device ID", "Alarm Type", ];
    const rows       = tableData.map((row) => [row.time, row.deviceId, row.msgType]);
    const csvContent = [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const blob       = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url        = window.URL.createObjectURL(blob);
    const link       = document.createElement("a");
    link.href        = url;
    const [year, month, day] = selDate.split("-");
    link.setAttribute("download", `alarmhistory-${day}-${month}-${year}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---------------- CALCULATIONS ---------------- */

  const daysInMonth = useMemo(
    () => new Date(selYear, selMonth + 1, 0).getDate(),
    [selYear, selMonth]
  );

  const firstDayOfWeek = useMemo(
    () => new Date(selYear, selMonth, 1).getDay(),
    [selYear, selMonth]
  );

  const countMap = useMemo(() => {
    const map      = {};
    const yearData = heatmapData.find((y) => y.year === selYear);
    if (!yearData) return map;

    const monthData = yearData.months.find((m) => m.month === selMonth + 1);
    if (!monthData) return map;

    monthData.days.forEach((dayObj) => {
      map[dateKey(selYear, selMonth, dayObj.day)] = dayObj.count;
    });
    return map;
  }, [heatmapData, selYear, selMonth]);

  const tableData = useMemo(
    () => historyData[selDate] || [],
    [selDate, historyData]
  );

  const selDateLabel = useMemo(() => {
    const [y, m, d] = selDate.split("-").map(Number);
    return `${MONTHS[m - 1]} ${d}, ${y}`;
  }, [selDate]);

  const noYearData = !heatmapData.some((y) => y.year === selYear);

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  /* ---------------- RENDER ---------------- */

  return (
    <Box sx={{ width: "100%", background: "#f8fafb" }}>
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "16px",
          background: "#ffffff",
          p: { xs: 1.5, sm: 2, md: 2.5 },
        }}
      >
        {/* ── HEADER ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: isExpanded ? { xs: 2, md: 3 } : 0,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* LEFT: title */}
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                lineHeight: 1.1,
              }}
            >
              Alarm History
            </Typography>
            <Typography
              sx={{ mt: 0.5, color: "#6b7280", fontSize: { xs: 12, sm: 13, md: 14 } }}
            >
              Archived log of all fall detector alarms
            </Typography>
          </Box>

          {/* RIGHT: toggle icon button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={() => setIsExpanded((prev) => !prev)}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: "10px",
                borderColor: "#d1d5db",
                color: "#6b7280",
                background: "#ffffff",
                p: 0,
              }}
            >
              {isExpanded
                ? <ExpandLessIcon sx={{ fontSize: 20 }} />
                : <ExpandMoreIcon sx={{ fontSize: 20 }} />
              }
            </Button>
          </Box>
        </Box>

        {/* ── COLLAPSIBLE BODY ── */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(280px, 340px) 1fr" },
              gap: 2,
            }}
          >
            {/* ── HEATMAP CARD ── */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                p: { xs: 1.5, sm: 2, md: 2.2 },
                background: "#fff",
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: { xs: 13, md: 15 }, mb: 2, color: "#111827" }}
              >
                Monthly Alarm Heatmap
              </Typography>

              {noYearData && (
                <Typography sx={{ mb: 2, fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>
                  No data available for {selYear}
                </Typography>
              )}

              {/* MONTH / YEAR SELECTORS */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Select
                  value={selMonth}
                  onChange={(e) => setSelMonth(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" } }}
                >
                  {MONTHS.map((m, i) => (
                    <MenuItem key={m} value={i}>{m}</MenuItem>
                  ))}
                </Select>

                <Select
                  value={selYear}
                  onChange={(e) => setSelYear(e.target.value)}
                  size="small"
                  sx={{ width: { xs: 85, md: 100 }, fontSize: { xs: "0.8rem", md: "0.875rem" } }}
                >
                  {YEARS.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </Box>

              {/* DAY LABELS */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7,1fr)",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                {DAY_LABELS.map((d) => (
                  <Typography
                    key={d}
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: 9, sm: 10, md: 11 },
                      fontWeight: 700,
                      color: "#6b7280",
                    }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>

              {/* CALENDAR GRID */}
              {heatmapLoading ? (
                <Box
                  sx={{
                    height: { xs: 200, md: 260 },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: { xs: 0.3, sm: 0.5 },
                  }}
                >
                  {cells.map((day, idx) => {
                    if (!day) return <Box key={idx} />;

                    const key        = dateKey(selYear, selMonth, day);
                    const count      = countMap[key] || 0;
                    const { bg, border, textColor } = getHeatColor(count); // ✅ no maxCount
                    const isSelected = key === selDate;

                    return (
                      <Button
                        key={key}
                        title={`${count} alarm${count !== 1 ? "s" : ""}`}
                        onClick={() => setSelDate(key)}
                        sx={{
                          minWidth: 0,
                          aspectRatio: "1",
                          borderRadius: { xs: 1, sm: 2 },
                          border: isSelected
                            ? "2px solid #111827"
                            : `1px solid ${border}`,
                          background: bg,
                          color: textColor,
                          fontWeight: 700,
                          fontSize: { xs: 9, sm: 11, md: 12 },
                          p: 0,
                          boxShadow: isSelected
                            ? "0 0 0 3px rgba(17,24,39,0.15)"
                            : "none",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {day}
                      </Button>
                    );
                  })}
                </Box>
              )}

              {/* ── LEGEND ── */}
              {!heatmapLoading && <HeatmapLegend />}  {/* ✅ no props needed */}
            </Card>

            {/* ── TABLE CARD ── */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                overflow: "hidden",
                background: "#fff",
                minHeight: { xs: "280px", md: "300px" },
                maxHeight: { xs: "350px", sm: "380px", md: "420px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* TABLE HEADER */}
              <Box
                sx={{
                  p: { xs: 1.5, md: 2.2 },
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: { xs: "0.85rem", md: "1rem" },
                  }}
                >
                  Logs for {selDateLabel}
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportCSV}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    borderColor: "#d1d5db",
                    color: "#111827",
                    background: "#ffffff",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.6,
                    minWidth: "auto",
                    fontSize: { xs: "0.72rem", md: "0.8rem" },
                    "&:hover": { background: "#f9fafb" },
                  }}
                >
                  Export CSV
                </Button>
              </Box>

              {historyLoading ? (
                <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <CircularProgress />
                </Box>
              ) : tableData.length === 0 ? (
                <Box sx={{ py: { xs: 5, md: 8 }, textAlign: "center" }}>
                  <Typography sx={{ color: "#6b7280", fontSize: { xs: "0.85rem", md: "1rem" } }}>
                    No alarms found
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ flex: 1, overflowY: "auto", overflowX: "auto" }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {["Timestamp", "Device ID", "Alarm Type", "Action"].map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              fontWeight: 700,
                              color: "#6b7280",
                              fontSize: { xs: 10, md: 12 },
                              whiteSpace: "nowrap",
                              px: { xs: 1, md: 2 },
                            }}
                          >
                            {col.toUpperCase()}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {tableData.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                              px: { xs: 1, md: 2 },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.time || "NA"}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: { xs: "0.7rem", md: "0.875rem" },
                              px: { xs: 1, md: 2 },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.deviceId || "NA"}
                          </TableCell>

                          <TableCell sx={{ px: { xs: 1, md: 2 } }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: { xs: 0.8, md: 1.2 },
                                py: 0.5,
                                borderRadius: 1.5,
                                background: "#FCEBEB",
                                color: "#A32D2D",
                                border: "1px solid #F09595",
                                fontSize: { xs: 9, md: 11 },
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.msgType || "NA"}
                            </Box>
                          </TableCell>

                          <TableCell sx={{ px: { xs: 1, md: 2 } }}>
                            <Button
                              size="small"
                              onClick={() => setDetailItem(row)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: { xs: "0.7rem", md: "0.8rem" },
                                px: { xs: 1, md: 1.5 },
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ── DETAIL POPUP ── */}
              <Dialog
                open={Boolean(detailItem)}
                onClose={() => setDetailItem(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                  sx: { pt: 2, borderRadius: "16px", mx: { xs: 2, sm: "auto" } },
                }}
              >
                <DialogTitle
                  sx={{
                    fontWeight: 700,
                    color: "#1f2937",
                    px: 3,
                    pt: 2,
                    pb: 2,
                    fontSize: { xs: "1rem", md: "1.25rem" },
                  }}
                >
                  Alarm Details
                </DialogTitle>

                <DialogContent sx={{ pt: 2, pb: 3, px: 3 }}>
                  {detailItem && (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Alarm Type</Typography>
                        <Typography sx={{ color: "#ef4444", fontWeight: 700 }}>
                          {detailItem.msgType || "NA"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Device ID</Typography>
                        <Typography>{detailItem.deviceId || "NA"}</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Name</Typography>
                        <Typography>******</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Location</Typography>
                        <Typography>******</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Timestamp</Typography>
                        <Typography>{detailItem.time || "NA"}</Typography>
                      </Grid>

                      {/* <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Payload</Typography>
                        <Typography
                          sx={{
                            wordBreak: "break-all",
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                          }}
                        >
                          {detailItem.payload || "NA"}
                        </Typography>
                      </Grid> */}
                    </Grid>
                  )}
                </DialogContent>
              </Dialog>
            </Card>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}